import { useState, useEffect } from 'react';
import LazyImage from '../../LazyImage';
import { formattedPrice } from '../../Cart/util';
import { formatCategory } from '../../../util';
import StrainIcon from '../StrainIcon';
import VariantSelect from '../VariantSelect';
import VariantBox from '../VariantBox';
import SpecialOffer from '../SpecialOffer';
import usePricingType from '../../../hooks/usePricingType.js';
import EnablePopupWrapper from './EnablePopupWrapper.jsx';
import { useConfig } from "../../../contexts/ConfigContext";
import { useAppState } from '../../../contexts/AppStateContext';

const ProductCardv2 = ({
    product,
    prioritySpecialObject,
    allSpecials = null,
    showMultipleSpecials = false,
    singleProductUrl,
    selectedVariant,
    setSelectedVariant,
    handleAddItem,
    variables,
    errors,
    columns,
    menuSlug,
    variantTheme,
    handleVariantBoxSelection,
    handleVariantSelection,
    isFirstProduct = false,
}) => {
    const { retailerId } = useAppState();
    
    const [pricingType] = usePricingType(retailerId);
    const [productSubcategory, setProductSubcategory] = useState(null);
    const hasMultipleVariants = product?.variants?.length > 1;
    
    // This logic determines if the main price/add-to-cart button should render for the whole card
    const shouldRenderPrimaryAddToCart =
        !variantTheme ||
        variantTheme.toLowerCase() === 'dropdown' ||
        (!hasMultipleVariants && (variantTheme.toLowerCase() === 'boxes' || variantTheme.toLowerCase() === 'boxes-slider'));

    // const handleTitleKeyPress = (e) => { // This function was defined but not used
    //     if (e.key === 'Enter' || e.key === ' ') {
    //         e.preventDefault();
    //         // Your popup logic here - you might need to expose this from EnablePopupWrapper
    //     }
    // };

    useEffect(() => {
        if (product?.subcategory && product.subcategory !== 'DEFAULT') {
            const formattedSubcategory = product.subcategory.replace(/_/g, ' ').toLowerCase(); // Often subcategories look better in title case or lower
            setProductSubcategory(formattedSubcategory.charAt(0).toUpperCase() + formattedSubcategory.slice(1));
        } else {
            setProductSubcategory(null); // Reset if no subcategory
        }
    }, [product?.subcategory]);


    if (!product || !product.variants || product.variants.length === 0 || !selectedVariant || !pricingType) {
        // Basic loading state or error display
        return <div className={`headless-product-card ${columns ? `product-card-cols-${columns}` : ''}`}>Loading product data...</div>;
    }

    return (
        <div
            className={`headless-product-card product-card-v2 ${columns ? `product-card-cols-${columns}` : ''}`} // Added product-card-v2 for specific styling
            data-product-id={product.id}
            role="article"
            aria-label={`${product.name} product details`}
        >
            {errors && errors.length > 0 && // Check if errors is defined
                errors.map((error, i) => (
                    <div className='headless-error-message' role="alert" key={i}>
                        <code>{typeof error === 'object' ? JSON.stringify(error) : error}</code>
                    </div>
                ))}

            {/* Render specials - either multiple or single */}
            <SpecialOffer
                specialData={prioritySpecialObject}
                allSpecials={allSpecials}
                multiSpecial={showMultipleSpecials}
                menuSlug={menuSlug}
            />

            <EnablePopupWrapper
                product={product}
                productUrl={singleProductUrl}
                classNames='headless-card-image-link'
            >
                <LazyImage
                    classnames='headless-product-image'
                    src={`${product.images[0].url}?w=250&fm=webp`}
                    alt={`${product.name} product image`}
                    fetchPriority={isFirstProduct ? "high" : undefined}
                />
            </EnablePopupWrapper>

            <div className='product-card-body'>
                <div className='product-card-body-top'>
                    {product.brand ? (
                        <div className='headless-product-brands'>
                            {product.brand.name}
                        </div>
                    ) : null}
                    <div className='headless-product-title-wrapper' role='presentation'>
                        <EnablePopupWrapper
                            product={product}
                            productUrl={singleProductUrl}
                            classNames='headless-product-title' // Use class for styling title if needed
                        >
                            {/* Product name length check was here, apply as needed or use CSS for truncation */}
                            {product.name}
                        </EnablePopupWrapper>
                    </div>
                </div>

                <div className='headless-product-bottom'> {/* This class might need v2 specific styling */}
                    <div className='headless-product-categories'>
                        {product.category ? formatCategory(product.category) : ''}
                        {productSubcategory && <span className='headless-product-subcategory'> - {productSubcategory}</span>}
                    </div>
                    <div className='headless-product-categories-wrapper'> {/* Consider renaming or v2 specific class */}
                        {product.strainType && product.strainType !== 'NOT_APPLICABLE' ? (
                            <div className='headless-product-strain'>
                                <StrainIcon strainType={product.strainType} />
                            </div>
                        ) : null}

                        {product.strainType && product.potencyThc?.formatted && ( // Check if potencyThc itself exists
                            <svg /* Decorative SVG, consider aria-hidden="true" */
                                xmlns='http://www.w3.org/2000/svg'
                                viewBox='0 0 512 512'
                                className="potency-separator-icon" // Added class for styling
                                aria-hidden="true"
                            >
                                <path d='M256 0c5.3 0 10.3 2.7 13.3 7.1c15.8 23.5 36.7 63.7 49.2 109c7.2 26.4 11.8 55.2 10.4 84c11.5-8.8 23.7-16.7 35.8-23.6c41-23.3 84.4-36.9 112.2-42.5c5.2-1 10.7 .6 14.4 4.4s5.4 9.2 4.4 14.5c-5.6 27.7-19.3 70.9-42.7 111.7c-9.1 15.9-19.9 31.7-32.4 46.3c27.8 6.6 52.4 17.3 67.2 25.5c5.1 2.8 8.2 8.2 8.2 14s-3.2 11.2-8.2 14c-15.2 8.4-40.9 19.5-69.8 26.1c-20.2 4.6-42.9 7.2-65.2 4.6l8.3 33.1c1.5 6.1-.6 12.4-5.5 16.4s-11.6 4.6-17.2 1.9L280 417.2V488c0 13.3-10.7 24-24 24s-24-10.7-24-24V417.2l-58.5 29.1c-5.6 2.8-12.3 2.1-17.2-1.9s-7-10.3-5.5-16.4l8.3-33.1c-22.2 2.6-45 0-65.2-4.6c-28.9-6.6-54.6-17.6-69.8-26.1c-5.1-2.8-8.2-8.2-8.2-14s3.2-11.2 8.2-14c14.8-8.2 39.4-18.8 67.2-25.5C78.9 296.3 68.1 280.5 59 264.6c-23.4-40.8-37.1-84-42.7-111.7c-1.1-5.2 .6-10.7 4.4-14.5s9.2-5.4 14.4-4.4c27.9 5.5 71.2 19.2 112.2 42.5c12.1 6.9 24.3 14.7 35.8 23.6c-1.4-28.7 3.1-57.6 10.4-84c12.5-45.3 33.4-85.5 49.2-109c3-4.4 8-7.1 13.3-7.1z' />
                            </svg>
                        )}

                        {product.potencyThc?.formatted ? ( // Check if potencyThc itself exists
                            <div className='headless-product-card-potency'>
                                THC {product.potencyThc.formatted}
                            </div>
                        ) : null}
                    </div>

                    {hasMultipleVariants && variantTheme && (
                        variantTheme.toLowerCase() === 'boxes' || variantTheme.toLowerCase() === 'boxes-slider' ? (
                            <VariantBox
                                variants={product.variants}
                                // setSelectedVariant={setSelectedVariant}
                                // handleAddItem={handleAddItem}
                                pricingType={pricingType}
                                // variables={variables}
                                handleVariantBoxSelection={handleVariantBoxSelection}
                                theme={variantTheme} // Pass theme to VariantBox
                                boxType='card'
                                selectedVariantProp={selectedVariant}
                            />
                        ) : variantTheme.toLowerCase() === 'dropdown' ? ( // Explicitly check for dropdown
                            <VariantSelect
                                product={product}
                                handleVariantSelection={handleVariantSelection}
                                selectedOptionId={selectedVariant?.id}
                            />
                        ) : null
                    )}

                    {shouldRenderPrimaryAddToCart && ( // Used to be shouldRender
                        <div className='headless-add-to-cart'> {/* This class might need v2 specific styling */}
                            {product.variants.length === 1 && selectedVariant.option && selectedVariant.option !== 'N/A' && (
                                <div className='headless-product-card-weight'>
                                    {selectedVariant.option}
                                </div>
                            )}
                            <div className='headless-product-card-price'>
                                {selectedVariant[pricingType.special] &&
                                    selectedVariant[pricingType.standard] &&
                                    selectedVariant[pricingType.special] < selectedVariant[pricingType.standard] ? (
                                    <>
                                        <span>
                                            <s className='headless-cart-item-price__strikethrough'>
                                                {formattedPrice(
                                                    selectedVariant[pricingType.standard]
                                                )}
                                            </s>{' '}
                                        </span>
                                        <span className='headless-pricing-type-special'>
                                            <strong>
                                                {formattedPrice(
                                                    selectedVariant[pricingType.special]
                                                )}
                                            </strong>
                                        </span>
                                    </>
                                ) : (
                                    selectedVariant[pricingType.standard] !== undefined && (
                                        <span>
                                            <strong>
                                                {formattedPrice(
                                                    selectedVariant[pricingType.standard]
                                                )}
                                            </strong>
                                        </span>
                                    )
                                )}
                            </div>

                            <button
                                className='headless-button headless-cart-button' // V2 might have its own button style
                                onClick={() => handleAddItem.mutate(variables)}
                                disabled={handleAddItem.isLoading}
                                aria-label={`Add ${product.name} to cart`} // Simplified aria-label, price can be complex here
                            >
                                <svg /* Add to cart Icon */
                                    xmlns='http://www.w3.org/2000/svg'
                                    viewBox='0 0 448 512'
                                    aria-hidden="true"
                                >
                                    <path d='M248 72c0-13.3-10.7-24-24-24s-24 10.7-24 24V232H40c-13.3 0-24 10.7-24 24s10.7 24 24 24H200V440c0 13.3 10.7 24 24 24s24-10.7 24-24V280H408c13.3 0-24-10.7-24-24s-10.7-24-24-24H248V72z' />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductCardv2;
